// src/lib/actionService.ts
//
// BekalNanti — Milestone P8: Action Authority (Next Step Layer)
// Pure, deterministic derivation of next steps from Trajectory, Reality Check, and Goal context.
// ZERO AI, ZERO synthetic financial advice.

import { PetaEsokTrajectoryMap } from './petaEsokTypes'
import { PetaEsokActionItem, ActionPriority } from './actionTypes'

/**
 * Derives concrete, context-aware Next Steps for an active goal trajectory.
 */
export function deriveTrajectoryActions(trajectory: PetaEsokTrajectoryMap): PetaEsokActionItem[] {
  const actions: PetaEsokActionItem[] = []
  const { goalId, bekal, unknowns, realityCheckStatus, isEducation, isCalculable, targetHorizonYear } = trajectory

  // 1. Missing data & Unknowns: Unknown costs or unconfirmed sources
  if (unknowns.length > 0) {
    unknowns.forEach((unk) => {
      let priority: ActionPriority = 'important'
      let actionType: PetaEsokActionItem['suggestedActionType'] = 'open_cost_funding'

      if (unk.category === 'cost') {
        priority = 'urgent'
        actionType = 'open_cost_funding'
      } else if (unk.category === 'prerequisite') {
        priority = 'suggested'
        actionType = 'open_research'
      }

      actions.push({
        id: `act-unk-${unk.id}`,
        goalId,
        title: `Lengkapi Estimasi: ${unk.title}`,
        description: `${unk.description} — ${unk.impact}`,
        category: 'missing_data',
        priority,
        sourceReason: `Ketidakpastian Kritis: ${unk.title}`,
        targetHorizonYear,
        suggestedActionType: actionType,
        isCompleted: false,
      })
    })
  }

  // 2. Reality Check Signals & Capacity: Respond to financial pressure or funding gaps
  if (realityCheckStatus === 'funding_gap') {
    actions.push({
      id: `act-rc-gap-${goalId}`,
      goalId,
      title: 'Tinjau Selisih Kapasitas Bulanan',
      description: `Target tabungan bulanan (Rp ${bekal.requiredMonthlySavingConfirmed.toLocaleString('id-ID')}) melebihi sisa kapasitas bulanan keluarga. Tinjau alokasi pengeluaran atau pertimbangkan skenario penyesuaian.`,
      category: 'reality_check_risk',
      priority: 'urgent',
      sourceReason: 'P6 Reality Check: funding_gap (Kapasitas tidak mencukupi)',
      targetHorizonYear,
      suggestedActionType: 'open_reality_check',
      isCompleted: false,
    })
  } else if (realityCheckStatus === 'funding_pressure') {
    actions.push({
      id: `act-rc-pressure-${goalId}`,
      goalId,
      title: 'Evaluasi Alokasi Tabungan Bulanan',
      description: 'Tabungan yang dibutuhkan memakan porsi besar kapasitas keluarga (>75%). Pertimbangkan proteksi dana darurat.',
      category: 'reality_check_risk',
      priority: 'important',
      sourceReason: 'P6 Reality Check: funding_pressure (Beban tabungan tinggi)',
      targetHorizonYear,
      suggestedActionType: 'open_reality_check',
      isCompleted: false,
    })
  }

  // 3. Estimated vs Confirmed Funding Review
  if (bekal.estimatedFundingTotal > 0) {
    actions.push({
      id: `act-fund-est-${goalId}`,
      goalId,
      title: 'Verifikasi Syarat & Peluang Beasiswa / Bantuan',
      description: `Terdapat estimasi pendanaan Rp ${bekal.estimatedFundingTotal.toLocaleString('id-ID')} yang belum terkonfirmasi. Pelajari syarat pendaftaran dan kriteria seleksi agar peluang terealisasi.`,
      category: 'funding_review',
      priority: 'important',
      sourceReason: 'P4 Pendanaan: Estimasi beasiswa/bantuan belum terkonfirmasi',
      targetHorizonYear,
      suggestedActionType: 'open_research',
      isCompleted: false,
    })
  }

  // 4. Milestone Preparation & Target Horizon
  if (trajectory.yearsRemaining !== null && trajectory.yearsRemaining <= 2 && trajectory.yearsRemaining > 0) {
    actions.push({
      id: `act-prep-horizon-${goalId}`,
      goalId,
      title: 'Persiapkan Berkas & Rekening Penampung Likuid',
      description: `Tinggal ${trajectory.yearsRemaining} tahun menuju masa target (${targetHorizonYear}). Mulai amankan instrumen simpanan ke instrumen berisiko rendah dan likuid.`,
      category: 'milestone_prep',
      priority: 'important',
      sourceReason: `Horizon Waktu: Sisa ${trajectory.yearsRemaining} tahun menuju pelaksanaan`,
      targetHorizonYear,
      suggestedActionType: 'open_finances',
      isCompleted: false,
    })
  }

  // 5. Research Hook (P11 Integration Point for any goal target)
  if (trajectory.targetInstitutionOrProgram) {
    actions.push({
      id: `act-res-target-${goalId}`,
      goalId,
      title: `Riset Informasi Terbaru: ${trajectory.targetInstitutionOrProgram}`,
      description: isEducation
        ? 'Lakukan penelusuran biaya kuliah teranyar, persyaratan berkas, atau opsi beasiswa langsung dari sumber resmi.'
        : 'Cari referensi modal awal, biaya operasional, atau standar kebutuhan pendanaan sasaran ini.',
      category: 'research_needed',
      priority: 'suggested',
      sourceReason: 'P11 Research Hook: Membutuhkan verifikasi biaya/syarat spesifik',
      targetHorizonYear,
      suggestedActionType: 'open_research',
      isCompleted: false,
    })
  }

  // 6. Completeness Check if Not Fully Calculable
  if (!isCalculable) {
    actions.push({
      id: `act-complete-spec-${goalId}`,
      goalId,
      title: 'Lengkapi Rincian Sasaran & Estimasi Biaya',
      description: 'Lengkapi tahun target dan minimal satu komponen biaya agar sistem dapat menghitung kurva tabungan dan reality check secara akurat.',
      category: 'missing_data',
      priority: 'important',
      sourceReason: 'Perencanaan Belum Lengkap: Membutuhkan tahun dan estimasi biaya',
      targetHorizonYear,
      suggestedActionType: 'open_target_spec',
      isCompleted: false,
    })
  }

  return actions
}
